import {FormSection} from '@/components/commons/form-section/FormSection'
import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext} from 'react-hook-form'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {InputText} from '@/components/commons/input-text/InputText'
import {CompanyInfoFormValidationSchema} from '@/features/company-info/types'
import {Switch} from '@/components/commons/switch/Switch'
import {Text} from '@/components/ui/text/Text'

export const GeneralInfoSection = () => {
    const {t} = useTranslation()
    const {
        register,
        formState: {errors},
        watch,
        control
    } = useFormContext<CompanyInfoFormValidationSchema>()
    const hasDifferentOperationalAddress = watch('hasDifferentOperationalAddress')

    return (
        <FormSection title={t('company_info:form:general_info_section_title')} hasBottomDivider>
            <Grid columns={2} gap={4}>
                <GridItem spanColumn={2}>
                    <InputText
                        label={t('company_info:form:company_name_label')}
                        type="text"
                        errorMessage={errors.name?.message}
                        touched={!!errors.name}
                        {...register('name')}
                    />
                </GridItem>
                <GridItem>
                    <InputText
                        label={t('company_info:form:company_p_iva_label')}
                        type="text"
                        errorMessage={errors.taxId?.message}
                        touched={!!errors.taxId}
                        {...register('taxId')}
                    />
                </GridItem>
                <GridItem>
                    <InputText
                        label={t('company_info:form:company_fiscal_code_label')}
                        type="text"
                        errorMessage={errors.fiscalCode?.message}
                        touched={!!errors.fiscalCode}
                        {...register('fiscalCode')}
                    />
                </GridItem>
            </Grid>

            <Text render="h3" size="textMd" weight="500" style={{padding: '16px 0'}}>
                {t('company_info:form:registered_office_label')}
            </Text>
            <Grid columns={6} gap={4}>
                <GridItem spanColumn={2}>
                    <InputText
                        label={t('company_info:form:legal_address_label')}
                        type="text"
                        errorMessage={errors.legalAddress?.message}
                        touched={!!errors.legalAddress}
                        {...register('legalAddress')}
                    />
                </GridItem>
                <GridItem spanColumn={2}>
                    <InputText
                        label={t('company_info:form:legal_city_label')}
                        type="text"
                        errorMessage={errors.legalCity?.message}
                        touched={!!errors.legalCity}
                        {...register('legalCity')}
                    />
                </GridItem>
                <GridItem>
                    <InputText
                        label={t('company_info:form:legal_province_label')}
                        type="text"
                        errorMessage={errors.legalProvince?.message}
                        touched={!!errors.legalProvince}
                        {...register('legalProvince')}
                    />
                </GridItem>
                <GridItem>
                    <InputText
                        label={t('commons:zip_code')}
                        type="text"
                        errorMessage={errors.legalZip?.message}
                        touched={!!errors.legalZip}
                        {...register('legalZip')}
                    />
                </GridItem>
            </Grid>

            <Text render="h3" size="textMd" weight="500" style={{padding: '16px 0'}}>
                {t('company_info:form:operative_address_label')}
            </Text>
            <GridItem>
                <Controller
                    control={control}
                    name="hasDifferentOperationalAddress"
                    render={({field: {onChange, value}}) => (
                        <Switch
                            label={t('company_info:form:different_from_legal_address_label')}
                            defaultChecked={value}
                            onChange={onChange}
                        />
                    )}
                />
            </GridItem>
            {hasDifferentOperationalAddress && (
                <Grid columns={6} gap={4} style={{padding: '16px 0'}}>
                    <GridItem spanColumn={2}>
                        <InputText
                            label={t('commons:address')}
                            type="text"
                            errorMessage={errors.operationalAddress?.message}
                            touched={!!errors.operationalAddress}
                            {...register('operationalAddress')}
                        />
                    </GridItem>
                    <GridItem spanColumn={2}>
                        <InputText
                            label={t('company_info:form:operative_city_label')}
                            type="text"
                            errorMessage={errors.operationalCity?.message}
                            touched={!!errors.operationalCity}
                            {...register('operationalCity')}
                        />
                    </GridItem>
                    <GridItem>
                        <InputText
                            label={t('company_info:form:operative_province_label')}
                            type="text"
                            errorMessage={errors.operationalProvince?.message}
                            touched={!!errors.operationalProvince}
                            {...register('operationalProvince')}
                        />
                    </GridItem>
                    <GridItem>
                        <InputText
                            label={t('commons:zip_code')}
                            type="text"
                            errorMessage={errors.operationalZip?.message}
                            touched={!!errors.operationalZip}
                            {...register('operationalZip')}
                        />
                    </GridItem>
                </Grid>
            )}

            <Grid columns={2} gap={4}>
                <GridItem>
                    <InputText
                        label={t('company_info:form:phone_number_label')}
                        type="tel"
                        errorMessage={errors.phone?.message}
                        touched={!!errors.phone}
                        {...register('phone')}
                    />
                </GridItem>
                <GridItem>
                    <InputText
                        label={t('company_info:form:email_label')}
                        type="email"
                        errorMessage={errors.email?.message}
                        touched={!!errors.email}
                        {...register('email')}
                    />
                </GridItem>
                <GridItem>
                    <InputText
                        label={t('company_info:form:pec_label')}
                        type="email"
                        errorMessage={errors.pec?.message}
                        touched={!!errors.pec}
                        {...register('pec')}
                    />
                </GridItem>
            </Grid>
        </FormSection>
    )
}
